import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { LayoutDashboard, Users, Building2, Wallet, UserCog, ArrowLeft, ShieldOff, ShieldCheck, Trash2 , ScrollText } from 'lucide-react';

import { adminApi, publicApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { Loader } from '@/components/Loader';

const links = [
  { to: '/admin', end: true, label: 'Tableau de bord', icon: LayoutDashboard },
  { to: '/admin/bailleurs', label: 'Bailleurs', icon: Building2 },
  { to: '/admin/locataires', label: 'Locataires', icon: Users },
  { to: '/admin/agents', label: 'Agents', icon: UserCog },
  { to: '/admin/abonnements', label: 'Abonnements', icon: Wallet },
  { to: '/admin/catalogue', label: 'Paramètres', icon: UserCog },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText },
];

export default function AdminBailleurDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getBailleur(id);
      setData(res.data);
    } catch {
      toast.error('Bailleur introuvable');
      nav('/admin/bailleurs');
    } finally { setLoading(false); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    publicApi.offers()
      .then(({ data: catalogue }) => setOffers(catalogue.items || []))
      .catch(() => toast.error('Impossible de charger le catalogue des offres.'));
  }, []);

  const toggle = async () => {
    await adminApi.toggleBailleur(id, !data.bailleur.is_active);
    toast.success(!data.bailleur.is_active ? 'Bailleur activé' : 'Bailleur désactivé');
    load();
  };
  const remove = async () => {
    if (!window.confirm('Supprimer définitivement ce bailleur et tous ses locataires ?')) return;
    await adminApi.deleteBailleur(id);
    toast.success('Bailleur supprimé');
    nav('/admin/bailleurs');
  };
  const anonymize = async () => {
    if (!window.confirm('RGPD — Anonymiser définitivement les données personnelles de ce bailleur ?\n\nCette action est IRRÉVERSIBLE. Les paiements et reçus sont conservés (traçabilité comptable) mais les informations personnelles (nom, email, téléphone, pièce d\'identité) seront effacées.')) return;
    try {
      await adminApi.anonymize(data.bailleur.user_id);
      toast.success('Compte anonymisé (RGPD)');
      nav('/admin/bailleurs');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };
  const changeOffer = async (offer) => {
    await adminApi.updateSubscription(id, { offer });
    toast.success(`Offre mise à jour: ${offer}`);
    load();
  };
  const removeLoc = async (locId) => {
    if (!window.confirm('Supprimer ce locataire ?')) return;
    await adminApi.deleteLocataire(locId);
    toast.success('Locataire supprimé');
    load();
  };

  return (
    <AppLayout links={links} testid={TID.adminSidebar} title={loading ? '' : `${data.bailleur.prenom} ${data.bailleur.nom}`}>
      <button onClick={() => nav('/admin/bailleurs')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0D1B2A] -mt-4 mb-4">
        <ArrowLeft className="w-4 h-4" /> Retour à la liste
      </button>
      {loading || !data ? null : (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div className="lb-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Profil bailleur</div>
                  <div className="mt-1 text-xl font-bold text-[#0D1B2A]">{data.bailleur.prenom} {data.bailleur.nom}</div>
                  <div className="text-sm text-slate-500">{data.bailleur.email} · {data.bailleur.telephone}</div>
                </div>
                {data.bailleur.is_active ? (
                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">Actif</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Inactif</Badge>
                )}
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                <div><div className="text-xs text-slate-500">Adresse</div><div className="text-[#0D1B2A]">{data.bailleur.adresse || '—'}</div></div>
                <div><div className="text-xs text-slate-500">Ville / Pays</div><div className="text-[#0D1B2A]">{data.bailleur.ville || '—'} · {data.bailleur.pays}</div></div>
                <div><div className="text-xs text-slate-500">Créé le</div><div className="text-[#0D1B2A]">{new Date(data.bailleur.created_at).toLocaleDateString('fr-FR')}</div></div>
                <div><div className="text-xs text-slate-500">Nb locataires</div><div className="text-[#0D1B2A] font-semibold">{data.locataires_count} / {data.subscription?.max_locataires || 0}</div></div>
                <div><div className="text-xs text-slate-500">Pièce d'identité</div><div className="text-[#0D1B2A]">{data.bailleur.type_piece || '—'} {data.bailleur.numero_piece ? `· ${data.bailleur.numero_piece}` : ''}</div></div>
              </div>
              {(data.bailleur.photo_piece_recto || data.bailleur.photo_piece_verso) && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {data.bailleur.photo_piece_recto && (
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Recto</div>
                      <img src={data.bailleur.photo_piece_recto} alt="Recto pièce" className="w-full max-h-48 object-contain rounded-lg border border-slate-200 bg-slate-50" />
                    </div>
                  )}
                  {data.bailleur.photo_piece_verso && (
                    <div>
                      <div className="text-xs text-slate-500 mb-1">Verso</div>
                      <img src={data.bailleur.photo_piece_verso} alt="Verso pièce" className="w-full max-h-48 object-contain rounded-lg border border-slate-200 bg-slate-50" />
                    </div>
                  )}
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-2">
                <Button variant="outline" onClick={toggle} data-testid="admin-toggle-bailleur">
                  {data.bailleur.is_active ? (<><ShieldOff className="w-4 h-4 mr-1.5" /> Désactiver</>) : (<><ShieldCheck className="w-4 h-4 mr-1.5" /> Activer</>)}
                </Button>
                <Button variant="outline" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={remove} data-testid="admin-delete-bailleur">
                  <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
                </Button>
              </div>
            </div>

            <div className="lb-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Locataires ({data.locataires.length})</div>
              </div>
              {data.locataires.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">Aucun locataire.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.locataires.map((l) => (
                    <div key={l.id} className="py-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-semibold text-[#0D1B2A]">{l.prenom} {l.nom}</div>
                        <div className="text-xs text-slate-500">{l.adresse_logement} · {l.montant_loyer} {l.devise}</div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => removeLoc(l.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="lb-card p-6">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Abonnement</div>
              <div className="mt-3 flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-[#0D1B2A] capitalize">{data.subscription?.offer}</div>
                  <div className="text-xs text-slate-500">Max {data.subscription?.max_locataires} locataires</div>
                </div>
                <Badge className="bg-[#E6F1FF] text-[#1677FF] hover:bg-[#E6F1FF]">${data.subscription?.price_usd}/an</Badge>
              </div>
              <div className="mt-4">
                <div className="text-xs text-slate-500 mb-1.5">Changer l'offre</div>
                <Select value={data.subscription?.offer} onValueChange={changeOffer}>
                  <SelectTrigger data-testid="admin-change-offer"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {offers.map((offer) => (
                      <SelectItem key={offer.code} value={offer.code}>
                        {offer.nom} · {offer.locataires_max} loc. · {Number(offer.prix_annuel_usd || 0) === 0 ? 'Gratuit' : `${offer.prix_annuel_usd} $/an`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4 text-xs text-slate-500">
                Statut : <span className="font-semibold text-[#0D1B2A] capitalize">{data.subscription?.status}</span><br />
                Expire le : {data.subscription?.expires_at ? new Date(data.subscription.expires_at).toLocaleDateString('fr-FR') : '—'}
              </div>
            </div>

            <div className="lb-card p-6 border-2 border-dashed border-red-200 bg-red-50/40">
              <div className="text-xs font-semibold uppercase tracking-widest text-red-700">Zone dangereuse</div>
              <div className="mt-3 space-y-2">
                <Button
                  data-testid="admin-bailleur-toggle"
                  variant="outline"
                  onClick={toggle}
                  className="w-full justify-start"
                >
                  {data.bailleur.is_active ? <><ShieldOff className="w-4 h-4 mr-2" /> Désactiver le compte</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Réactiver le compte</>}
                </Button>
                <Button
                  data-testid="admin-bailleur-anonymize"
                  variant="outline"
                  onClick={anonymize}
                  className="w-full justify-start border-amber-300 text-amber-800 hover:bg-amber-50"
                >
                  Anonymiser (RGPD)
                </Button>
                <Button
                  data-testid="admin-bailleur-delete"
                  variant="outline"
                  onClick={remove}
                  className="w-full justify-start border-red-300 text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement
                </Button>
              </div>
              <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                <strong>Anonymiser</strong> : conserve les paiements/reçus mais efface les données personnelles (RGPD).<br />
                <strong>Supprimer</strong> : archive tout, y compris les locataires liés.
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
